$component-class: 'me-button';
$component-icon: 'me-icon';

// Base styles
$button-base: (
  font-size: map-get($font-sizes, md),
  line-height: map-get($line-heights, md),
  font-weight: map-get($font-weights, semi-bold),
  border-radius: var(--radius-size-md),
  border-width: var(--border-size-sm),
  transition: all 0.2s ease-in-out,
  padding-y: calc(var(--spacing-sm) - var(--border-size-sm)),
  padding-x: calc(var(--spacing-xl) - var(--border-size-sm)),
  loading: (
    color: transparent,
    field-radius: var(--radius-size-sm),
    field-background-color-light: unquote('color-mix(in srgb, var(--color-neutral-white) 15%, transparent)'),
    field-background-color-dark: unquote('color-mix(in srgb, var(--color-neutral-neutral-dark) 20%, transparent)'),
  ),
  focus: (
    outline-border-width: var(--border-size-md),
    outline-spread: calc(var(--border-size-md) * 3),
    outline-radius: calc(var(--border-size-md) * 6),
    outline-border: solid var(--border-size-md) var(--color-brand-primary-light),
  ),
  small: (
    padding-y: calc(var(--spacing-xs) - var(--border-size-sm)),
    padding-x: calc(var(--spacing-lg) - var(--border-size-sm)),
    icon-only-padding: var(--spacing-xs),
  ),
  icon: (
    size: 1.5rem,
    margin: var(--spacing-sm),
  ),
  icon-only: (
    padding: var(--spacing-sm),
    loading-margin: calc(var(--spacing-sm) + var(--border-size-lg)),
    ghost-loading-margin: var(--border-size-lg),
  ),
);

// Button variants — colors adapt automatically via CSS variable cascade from parent color mode
$button-variants: (
  primary: (
    color: var(--text-color-alternate),
    border-color: var(--action-color-brand-primary),
    background-color: var(--action-color-brand-primary),
    hover: (
      color: var(--text-color-alternate),
      border-color: var(--action-color-brand-primary-hover),
      background-color: var(--action-color-brand-primary-hover),
    ),
  ),
  secondary: (
    color: var(--text-color-primary),
    border-color: var(--action-color-brand-primary),
    background-color: transparent,
    hover: (
      color: var(--text-color-primary),
      border-color: var(--action-color-brand-primary),
      background-color: transparent,
      box-shadow: inset 0px 0px 0px var(--border-size-sm) var(--action-color-brand-primary),
    ),
  ),
  tertiary: (
    color: var(--text-color-primary),
    border-color: transparent,
    background-color: transparent,
    hover: (
      color: var(--action-color-primary-hover),
      border-color: transparent,
      background-color: transparent,
    ),
  ),
  detailed: (
    color: var(--text-color-primary),
    border-color: transparent,
    background-color: transparent,
    hover: (
      color: var(--action-color-primary-hover),
      border-color: transparent,
      background-color: transparent,
    ),
  ),
  ghost: (
    color: var(--text-color-primary),
    border-color: transparent,
    background-color: transparent,
    hover: (
      color: var(--text-color-primary),
      border-color: transparent,
      background-color: transparent,
      icon-only-color: var(--action-color-primary-hover),
    ),
  ),
  bordered: (
    color: var(--text-color-alternate),
    border-color: var(--border-color-alternate),
    background-color: transparent,
    hover: (
      color: var(--text-color-alternate),
      border-color: var(--border-color-alternate),
      background-color: unquote('color-mix(in srgb, var(--border-color-alternate) 15%, transparent)'),
    ),
  ),
);

// Helper functions
@function get-button-variant($variant, $property) {
  @return map-get(map-get($button-variants, $variant), $property);
}

@function get-button-state($variant, $state, $property) {
  @return map-get(map-get(map-get($button-variants, $variant), $state), $property);
}

@mixin button-variant($variant) {
  background-color: get-button-variant($variant, background-color);
  color: get-button-variant($variant, color);
  border-color: get-button-variant($variant, border-color);

  &.is-hovered,
  &:hover {
    background-color: get-button-state($variant, hover, background-color);
    border-color: get-button-state($variant, hover, border-color);
    color: get-button-state($variant, hover, color);
  }
}

@keyframes loading {
  to {
    opacity: 0;
  }
}

// Base button styles
.#{$component-class} {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  vertical-align: middle;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  user-select: none;
  touch-action: manipulation;
  -webkit-font-smoothing: antialiased;
  background-image: none;
  font-weight: map-get($button-base, font-weight);
  border-radius: map-get($button-base, border-radius);
  font-size: map-get($button-base, font-size);
  line-height: map-get($button-base, line-height);
  padding: map-get(map-get($button-base, small), padding-y) map-get(map-get($button-base, small), padding-x);
  transition: map-get($button-base, transition);
  border: map-get($button-base, border-width) solid;

  @include desktop {
    padding: map-get($button-base, padding-y) map-get($button-base, padding-x);
  }

  &::after {
    content: '';
    position: absolute;
    opacity: 0;
    box-sizing: border-box;
    pointer-events: none;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    margin: -#{map-get(map-get($button-base, focus), outline-spread)};
    border: map-get(map-get($button-base, focus), outline-border);
    transition: map-get($button-base, transition);
    border-radius: map-get(map-get($button-base, focus), outline-radius);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 var(--border-size-md) var(--color-neutral-white),
      0 0 0 var(--border-size-lg) var(--color-brand-primary-light);

    &::after {
      opacity: 0;
    }
  }

  &.is-disabled,
  &[disabled],
  &[aria-disabled='true'],
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    user-select: none;
    pointer-events: none;
  }

  &.is-loading {
    pointer-events: none;
    user-select: none;
    cursor: wait;
    position: relative;
    color: map-get(map-get($button-base, loading), color) !important;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      height: auto;
      width: auto;
      animation: loading 2s linear infinite alternate;
      border-radius: map-get(map-get($button-base, loading), field-radius);
      margin: calc(map-get($button-base, padding-y) + var(--border-size-lg)) calc(map-get($button-base, padding-x) + var(--border-size-lg));
      background-color: map-get(map-get($button-base, loading), field-background-color-dark);
    }
  }
}

.#{$component-class}--sm {
  padding: map-get(map-get($button-base, small), padding-y) map-get(map-get($button-base, small), padding-x);

  &.is-loading {
    &::before {
      margin: calc(map-get(map-get($button-base, small), padding-y) + var(--border-size-lg)) calc(map-get(map-get($button-base, small), padding-x) + var(--border-size-lg));
    }
  }

  &.#{$component-class}--icon-only {
    padding: map-get(map-get($button-base, small), icon-only-padding);

    &.is-loading {
      &::before {
        margin: calc(map-get(map-get($button-base, small), icon-only-padding) + var(--border-size-lg));
      }
    }
  }
}

.#{$component-class}--primary {
  @include button-variant(primary);

  &.is-loading {
    &::before {
      background-color: map-get(map-get($button-base, loading), field-background-color-light);
    }
  }
}

.#{$component-class}--secondary {
  @include button-variant(secondary);

  &.is-hovered,
  &:hover {
    box-shadow: get-button-state(secondary, hover, box-shadow);
  }
}

.#{$component-class}--tertiary {
  @include button-variant(tertiary);

  &.#{$component-class}--icon-only {
    &.is-hovered,
    &:hover {
      color: get-button-state(tertiary, hover, color);
    }
  }
}

.#{$component-class}--detailed {
  @include button-variant(detailed);

  align-items: flex-start;

  .#{$component-class}__content {
    text-align: left;
  }

  .#{$component-class}__description {
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
  }

  &.#{$component-class}--icon-only {
    &.is-hovered,
    &:hover {
      color: get-button-state(detailed, hover, color);
    }
  }
}

.#{$component-class}--bordered {
  @include button-variant(bordered);
}

.#{$component-class}--ghost {
  border: 0;
  padding: 0;
  border-radius: 0;

  @include button-variant(ghost);

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    width: 0;
    background-color: get-button-variant(ghost, color);
    height: var(--border-size-md);
    transition: map-get($button-base, transition);
  }

  &::after {
    border-radius: 0;
  }

  &.is-hovered,
  &:hover {
    &::before {
      width: 100%;
    }
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow:
      0 0 0 var(--border-size-md) var(--color-neutral-white),
      0 0 0 var(--border-size-lg) var(--color-brand-primary-light);

    &::before {
      display: none;
    }
  }

  &.is-loading {
    &::before {
      margin: map-get(map-get($button-base, icon-only), ghost-loading-margin);
    }
  }

  &.#{$component-class}--icon-before {
    &::before {
      width: 0;
      left: calc(map-get(map-get($button-base, icon), margin) + map-get(map-get($button-base, icon), size));
    }

    &.is-hovered,
    &:hover {
      &::before {
        width: calc(100% - (map-get(map-get($button-base, icon), margin) + map-get(map-get($button-base, icon), size)));
      }
    }

    &.is-loading {
      &::before {
        left: 0;
        width: auto;
      }
    }
  }

  &.#{$component-class}--icon-after {
    &::before {
      width: 0;
      right: calc(map-get(map-get($button-base, icon), margin) + map-get(map-get($button-base, icon), size));
    }

    &.is-hovered,
    &:hover {
      &::before {
        width: calc(100% - map-get(map-get($button-base, icon), size));
      }
    }

    &.is-loading {
      &::before {
        left: 0;
        width: auto;
      }
    }
  }

  &.#{$component-class}--icon-only {
    padding: 0;

    &::before {
      width: 0;
    }

    &.is-hovered,
    &:hover {
      color: get-button-state(ghost, hover, icon-only-color);
    }

    &.is-loading {
      &::before {
        margin: map-get(map-get($button-base, icon-only), ghost-loading-margin);
        left: 0;
        width: auto;
      }
    }
  }
}

.#{$component-class}--icon-before {
  .#{$component-icon} {
    margin-right: map-get(map-get($button-base, icon), margin);
  }
}

.#{$component-class}--icon-after {
  .#{$component-icon} {
    margin-left: map-get(map-get($button-base, icon), margin);
  }
}

.#{$component-class}--icon-only {
  padding: map-get(map-get($button-base, icon-only), padding);

  .#{$component-icon} {
    @include size(map-get(map-get($button-base, icon), size));
  }

  &.is-loading {
    &::before {
      margin: map-get(map-get($button-base, icon-only), loading-margin);
    }
  }
}
